/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import React from 'react'

/** A description of the available properties for this component. */
type FlowConnectorLineProps = {
  /** The width of the horizontal section of the line. */
  horizontalWidth: number

  /**
   * The padding on the left and right of the line. This is a percentage of the horizontal width (as a whole number).
   * For example a value of '10' would mean the padding is 10% of the horizontal width.
   */
  horizontalPadding: number

  /** The height of the vertical section of the line. */
  verticalHeight: number

  /** The curvature of the line. */
  curvature: number

  /** The primary colour of the line. */
  colour: string

  /** The width/thickness of the line. */
  strokeWidth?: number

  /** The style of the line. */
  lineStyle?: 'solid' | 'dashed' | 'dotted' | 'dashdot' | 'doubleLines' | 'gradient'

  /** The secondary colour of the line (for double lines and gradients). */
  secondaryColour?: string

  /** The direction of the line. */
  direction: 'leftToRight' | 'rightToLeft'
}

/**
 * A component that renders a line with a specified curvature. Intended to be used in the flow content wrapper,
 * connecting the flow content items together.
 */
const FlowConnectorLine: React.FC<FlowConnectorLineProps> = ({
  horizontalWidth,
  horizontalPadding,
  verticalHeight,
  curvature,
  colour,
  strokeWidth = 2,
  lineStyle = 'solid',
  secondaryColour,
  direction,
}) => {
  const paddedHorizontalWidth = horizontalWidth * ((100 - horizontalPadding) / 100)
  const adjustedCurvature = Math.min(curvature, verticalHeight)
  const strokePadding = strokeWidth / 2
  const viewBoxWidth = paddedHorizontalWidth + strokeWidth
  const viewBoxHeight = verticalHeight * 2 + adjustedCurvature + strokeWidth
  const halfVerticalHeight = verticalHeight / 2 + strokeWidth * 1.5

  const mainPath =
    direction === 'leftToRight'
      ? `
      M${strokePadding} ${strokePadding} 
      V${halfVerticalHeight + strokePadding}
      Q${strokePadding} ${halfVerticalHeight + adjustedCurvature} ${strokePadding + adjustedCurvature} ${halfVerticalHeight + adjustedCurvature} 
      H${paddedHorizontalWidth - adjustedCurvature + strokePadding} 
      Q${paddedHorizontalWidth + strokePadding} ${halfVerticalHeight + adjustedCurvature} ${paddedHorizontalWidth + strokePadding} ${halfVerticalHeight + adjustedCurvature * 2}
      V${viewBoxHeight - strokePadding}
    `
      : `
      M${viewBoxWidth - strokePadding} ${strokePadding} 
      V${halfVerticalHeight + strokePadding}
      Q${viewBoxWidth - strokePadding} ${halfVerticalHeight + adjustedCurvature} ${viewBoxWidth - strokePadding - adjustedCurvature} ${halfVerticalHeight + adjustedCurvature} 
      H${adjustedCurvature + strokePadding} 
      Q${strokePadding} ${halfVerticalHeight + adjustedCurvature} ${strokePadding} ${halfVerticalHeight + adjustedCurvature * 2}
      V${viewBoxHeight - strokePadding}
    `

  const getStrokeDasharray = () => {
    switch (lineStyle) {
      case 'dashed':
        return `${strokeWidth * 3} ${strokeWidth * 2}`
      case 'dotted':
        return `${strokeWidth} ${strokeWidth * 2}`
      case 'dashdot':
        return `${strokeWidth * 3} ${strokeWidth * 2} ${strokeWidth} ${strokeWidth * 2}`
      default:
        return 'none'
    }
  }

  return (
    <div style={{ width: `${viewBoxWidth}px` }}>
      <svg
        xmlns={'http://www.w3.org/2000/svg'}
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        style={{ maxWidth: '100%', height: 'auto' }}
      >
        {lineStyle === 'gradient' && (
          <defs>
            <linearGradient id={'lineGradient'} x1={'0%'} y1={'0%'} x2={'100%'} y2={'0%'}>
              <stop offset={'0%'} stopColor={colour} />
              <stop offset={'100%'} stopColor={secondaryColour || colour} />
            </linearGradient>
          </defs>
        )}
        {lineStyle === 'doubleLines' && (
          <path
            d={mainPath}
            fill={'none'}
            stroke={secondaryColour || colour}
            strokeWidth={strokeWidth * 2}
            strokeLinecap={'round'}
          />
        )}
        <path
          d={mainPath}
          fill={'none'}
          stroke={lineStyle === 'gradient' ? 'url(#lineGradient)' : colour}
          strokeWidth={lineStyle === 'doubleLines' ? strokeWidth : strokeWidth}
          strokeLinecap={'round'}
          strokeDasharray={getStrokeDasharray()}
        />
      </svg>
    </div>
  )
}

export { FlowConnectorLine }
export type { FlowConnectorLineProps }
